import { Fragment, useState } from "react";

import { Badge } from "@bigbinary/neeto-atoms";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
} from "@bigbinary/neeto-atoms/primitives";
import { ChevronRight } from "lucide-react";
import { isNot, hyphenate, isNotEmpty, isPresent } from "neetocist";
import useLocalStorage from "neetocommons/react-utils/useLocalStorage";
import { append, is } from "ramda";
import { useLocation } from "react-router-dom";

import AddAction from "./AddAction";
import CheckPointNavLinks from "./CheckPointNavLink";
import MoreDropdown from "./MoreDropdown";
import SubLinkItem from "./SubLinkItem";

import { PINNED_MORE_NAV_LINKS_KEY } from "../constants";
import { filterByPermissions } from "../utils";

const renderCount = count => (count > 999 ? "999+" : count);

const NavLinks = ({
  navLinks,
  moreNavLinks,
  isCountsLoading: isCountsLoadingDeprecated,
  setSelectedNavLinkRoute,
}) => {
  const location = useLocation();
  const [expandedNavLinkKeys, setExpandedNavLinkKeys] = useState([]);
  const [pinnedNavLinks, setPinnedNavLinks] = useLocalStorage(
    PINNED_MORE_NAV_LINKS_KEY,
    []
  );

  const handleNavLinkClick = ({ event, isActive, navLink }) => {
    const isExpandable =
      isPresent(navLink.items) || isPresent(navLink.component);
    if (!isExpandable) {
      setExpandedNavLinkKeys([]);

      return;
    }

    if (navLink.isConfigureNavLink && isNotEmpty(navLink.items)) {
      setSelectedNavLinkRoute(navLink.to);
    }

    const navLinkKey = navLink.key || navLink.to;
    if (isActive()) {
      const isExpanded = expandedNavLinkKeys.includes(navLinkKey);
      setExpandedNavLinkKeys(isExpanded ? [] : [navLinkKey]);
      navLink.resetPath !== true && event.preventDefault();

      return;
    }

    setExpandedNavLinkKeys([navLinkKey]);
  };

  const handleTogglePin = (navLink, event) => {
    event.preventDefault();
    event.stopPropagation();
    const navLinkKey = navLink.key || navLink.to;
    const isPinned = pinnedNavLinks.includes(navLinkKey);

    if (isPinned) {
      setPinnedNavLinks(pinnedNavLinks.filter(isNot(navLinkKey)));
    } else {
      setPinnedNavLinks([...pinnedNavLinks, navLinkKey]);
    }
  };

  const isNavLinkPinned = navLink => {
    if (!navLink) return false;
    const navLinkKey = navLink.key || navLink.to;

    return pinnedNavLinks.includes(navLinkKey);
  };

  const pinnedItems = moreNavLinks ? moreNavLinks.filter(isNavLinkPinned) : [];

  const activeMoreNavLink = moreNavLinks
    ? moreNavLinks.find(
        navLink =>
          navLink &&
          (location.pathname + location.search).startsWith(navLink.to)
      )
    : null;

  const additionalItems = [...pinnedItems];

  if (activeMoreNavLink && !isNavLinkPinned(activeMoreNavLink)) {
    additionalItems.push(activeMoreNavLink);
  }

  const combinedNavLinks = [...navLinks, ...additionalItems];

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu className="gap-1">
          {combinedNavLinks
            .filter(filterByPermissions)
            .map((navLink, index) => {
              const {
                label,
                to,
                icon,
                items,
                isConfigureNavLink,
                isCountsLoading = false,
                count = null,
                component: Component,
                onClick,
                addActions = [],
                tag,
                ...otherProps
              } = navLink;
              const IconSVG = icon;
              const url = new URL(to, window.location.href);
              const isActive = () =>
                is(Function, otherProps.isActive)
                  ? otherProps.isActive()
                  : location.pathname.startsWith(url.pathname);

              const isExpandable = items || Component;
              const navLinkKey = navLink.key || navLink.to;
              const isMenuExpanded =
                expandedNavLinkKeys.includes(navLinkKey) &&
                !isCountsLoadingDeprecated;

              const handleCaretClick = event => {
                event.preventDefault();
                event.stopPropagation();
                setExpandedNavLinkKeys(keys =>
                  keys.includes(navLinkKey)
                    ? keys.filter(isNot(navLinkKey))
                    : append(navLinkKey, keys)
                );
              };

              return (
                <Fragment key={navLink.key || index}>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive()}>
                      <CheckPointNavLinks
                        activeClassName="active"
                        className="font-medium"
                        data-testid={`navlink-${hyphenate(label)}`}
                        onClick={event => {
                          handleNavLinkClick({ event, isActive, navLink });
                          onClick?.(event);
                        }}
                        {...{ isActive, to, ...otherProps }}
                      >
                        {icon && (
                          <span className="relative" data-testid={label}>
                            <IconSVG />
                            {isPresent(count) && !isCountsLoading && (
                              <span className="bg-destructive text-destructive-foreground absolute -end-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-0.5 text-[10px]">
                                {renderCount(count)}
                              </span>
                            )}
                          </span>
                        )}
                        <span className="flex-grow truncate">{label}</span>
                        {tag && <Badge {...tag} />}
                        {isExpandable && !isConfigureNavLink && (
                          <span
                            className="ms-auto shrink-0 transition-transform duration-200"
                            role="button"
                            tabIndex={-1}
                            style={{
                              transform: isMenuExpanded
                                ? "rotate(90deg)"
                                : "rotate(0deg)",
                            }}
                            onClick={handleCaretClick}
                          >
                            <ChevronRight className="rtl:rotate-180" />
                          </span>
                        )}
                      </CheckPointNavLinks>
                    </SidebarMenuButton>
                    {isNotEmpty(addActions) && (
                      <AddAction actions={addActions} />
                    )}
                  </SidebarMenuItem>
                  {isExpandable && !isConfigureNavLink && isMenuExpanded && (
                    <>
                      {items && (
                        <SidebarMenuSub>
                          {items
                            .filter(filterByPermissions)
                            .map((subItem, subIndex) => (
                              <SubLinkItem
                                key={subIndex}
                                {...{ ...subItem, isCountsLoading }}
                                href={subItem.href}
                                isActive={subItem.isActive}
                                isSectionHeader={isPresent(subItem.item)}
                                to={subItem.to ?? subItem.path}
                                onClick={subItem.onClick}
                              />
                            ))}
                        </SidebarMenuSub>
                      )}
                      {Component && <Component {...{ navLink }} />}
                    </>
                  )}
                </Fragment>
              );
            })}
          {isNotEmpty(moreNavLinks) && (
            <MoreDropdown
              {...{ handleTogglePin, isNavLinkPinned, moreNavLinks }}
            />
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export default NavLinks;
